const features = [
  {
    icon: 'ri-robot-line',
    title: 'AI Voice & Chat Agents',
    desc: 'Never miss another opportunity. Our AI agents handle inbound calls and chats 24/7, qualifying leads and booking appointments automatically.',
    tag: 'Always On',
    img: 'https://public.readdy.ai/ai/img_res/5c24c7abcbd7d3d733ad9faabfa0e28a.jpg',
  },
  {
    icon: 'ri-database-2-line',
    title: 'Unified CRM Systems',
    desc: 'All your data, one place. Contacts, conversations, deals, and history — unified into a single source of truth your whole team can act on.',
    tag: 'Single Source',
    img: 'https://readdy.ai/api/search-image?query=clean%20modern%20CRM%20dashboard%20with%20contact%20cards%20pipeline%20view%20and%20data%20visualization%20charts%2C%20minimal%20dark%20interface%20with%20violet%20and%20indigo%20highlights%2C%20professional%20software%20UI%2C%20dark%20navy%20theme&width=600&height=400&seq=feat-crm-v2&orientation=landscape',
  },
  {
    icon: 'ri-send-plane-line',
    title: 'Automated Follow-Up',
    desc: 'Consistent engagement without effort. Multi-channel follow-up sequences that run automatically — SMS, email, and voice — triggered by behavior.',
    tag: 'Multi-Channel',
    img: 'https://readdy.ai/api/search-image?query=automated%20email%20and%20SMS%20workflow%20diagram%20with%20connected%20nodes%20and%20message%20templates%20on%20dark%20background%2C%20violet%20and%20indigo%20color%20scheme%2C%20business%20automation%20visualization%2C%20glowing%20connections&width=600&height=400&seq=feat-followup-v2&orientation=landscape',
  },
  {
    icon: 'ri-bar-chart-2-line',
    title: 'Pipeline Visibility',
    desc: 'Know exactly where revenue stands. Real-time dashboards that show every deal, every stage, and every opportunity — no guesswork.',
    tag: 'Real-Time',
    img: 'https://readdy.ai/api/search-image?query=revenue%20pipeline%20dashboard%20with%20funnel%20chart%20and%20deal%20stages%20visualization%2C%20dark%20minimal%20interface%20with%20glowing%20data%20points%20and%20violet%20indigo%20accent%20colors%2C%20business%20analytics%2C%20dark%20navy%20background&width=600&height=400&seq=feat-pipeline-v2&orientation=landscape',
  },
  {
    icon: 'ri-settings-3-line',
    title: 'Workflow Automation',
    desc: 'Remove manual bottlenecks. Automate repetitive tasks, approvals, and handoffs so your team focuses on high-value work.',
    tag: 'Zero Friction',
    img: 'https://readdy.ai/api/search-image?query=workflow%20automation%20diagram%20with%20connected%20process%20nodes%20and%20automation%20triggers%20on%20dark%20background%2C%20modern%20business%20process%20visualization%20with%20violet%20indigo%20and%20dark%20navy%20colors%2C%20glowing%20node%20connections&width=600&height=400&seq=feat-workflow-v2&orientation=landscape',
  },
];

export default function Features() {
  return (
    <section id="features" className="bg-[#07091a] py-24 md:py-32 relative overflow-hidden">
      <div className="absolute inset-0 opacity-5 pointer-events-none"
        style={{ backgroundImage: 'linear-gradient(rgba(139,92,246,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(139,92,246,0.3) 1px, transparent 1px)', backgroundSize: '60px 60px' }}>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-10 relative z-10">
        <div className="text-center mb-16 md:mb-20">
          <div className="inline-flex items-center gap-2 bg-violet-500/10 border border-violet-500/20 rounded-full px-4 py-1.5 mb-6">
            <span className="w-2 h-2 rounded-full bg-violet-400 flex-shrink-0"></span>
            <span className="text-violet-300 text-xs font-medium tracking-widest uppercase whitespace-nowrap">Core Features</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight">
            Everything Your Business Needs.<br />
            <span className="text-white/30">Nothing It Doesn't.</span>
          </h2>
        </div>

        {/* Feature grid: 3 top + 2 bottom */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-5">
          {features.slice(0, 3).map((f, i) => (
            <FeatureCard key={i} feature={f} highlight={i === 1} />
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {features.slice(3).map((f, i) => (
            <FeatureCard key={i} feature={f} highlight={false} wide />
          ))}
        </div>
      </div>
    </section>
  );
}

function FeatureCard({ feature, highlight, wide }: { feature: typeof features[0]; highlight: boolean; wide?: boolean }) {
  return (
    <div className={`rounded-2xl overflow-hidden border transition-all hover:border-violet-500/30 group ${highlight ? 'bg-gradient-to-br from-violet-900/40 to-indigo-900/30 border-violet-500/20' : 'bg-white/3 border-white/8'}`}>
      {/* Image */}
      <div className={`w-full overflow-hidden ${wide ? 'h-44' : 'h-48'}`}>
        <img
          src={feature.img}
          alt={feature.title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      {/* Content */}
      <div className="p-6">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-violet-500/15 flex-shrink-0">
            <i className={`${feature.icon} text-violet-400 text-base`}></i>
          </div>
          <span className="text-xs font-semibold text-violet-400/80 bg-violet-500/10 px-2.5 py-1 rounded-full whitespace-nowrap">{feature.tag}</span>
        </div>
        <h3 className="text-white font-bold text-base mb-2">{feature.title}</h3>
        <p className="text-white/40 text-xs leading-relaxed">{feature.desc}</p>
      </div>
    </div>
  );
}
